import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TableSortLabel, TextField, Typography, Paper
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { useContext, useEffect, useState } from "react";
import { CreateThreadType, ThreadsType } from "types/ThreadsType";
import { getThreads, createThread, updateThread, disableThread } from "util/network/Threads";

const emptyForm: CreateThreadType = {
    category: "",
    name: "",
    threadDiameter: 0,
    pitch: 0,
    minDiameter: 0,
    maxDiameter: 0,
    minDepth: 0,
    maxDepth: 0,
    color: { r: 0, g: 0, b: 0 },
    startPrice: 0,
    perAdditionalPrice: 0,
};

const ThreadsManagement: React.FC = () => {
    const { token } = useContext(AppStateContext);
    const { addToast } = useToast();

    const [threads, setThreads] = useState<ThreadsType[]>([]);
    const [loading, setLoading] = useState(true);
    const [editThread, setEditThread] = useState<ThreadsType | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<ThreadsType | null>(null);
    const [form, setForm] = useState<CreateThreadType>(emptyForm);
    const [saving, setSaving] = useState(false);
    const [sortBy, setSortBy] = useState<"name" | "category" | "startPrice" | "perAdditionalPrice">("category");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

    const fetchAll = async () => {
        try {
            const data = await getThreads(token);
            setThreads(data);
        } catch {
            addToast({ type: "error", message: "Failed to fetch threads" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const openCreate = () => {
        setEditThread(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEdit = (t: ThreadsType) => {
        setEditThread(t);
        setForm({
            category: t.category,
            name: t.name,
            threadDiameter: t.threadDiameter,
            pitch: t.pitch,
            minDiameter: t.minDiameter,
            maxDiameter: t.maxDiameter,
            minDepth: t.minDepth,
            maxDepth: t.maxDepth,
            color: { ...t.color },
            startPrice: t.startPrice / 100,
            perAdditionalPrice: t.perAdditionalPrice / 100,
        });
        setDialogOpen(true);
    };

    const openDelete = (t: ThreadsType) => {
        setDeleteTarget(t);
        setDeleteDialogOpen(true);
    };

    const handleSave = async () => {
        setSaving(true);
        const payload = {
            ...form,
            startPrice: Math.round(form.startPrice * 100),
            perAdditionalPrice: Math.round(form.perAdditionalPrice * 100),
        };
        try {
            if (editThread) {
                await updateThread(token, editThread.id, payload);
                addToast({ type: "success", message: "Thread updated" });
            } else {
                await createThread(token, payload);
                addToast({ type: "success", message: "Thread created" });
            }
            setDialogOpen(false);
            await fetchAll();
        } catch {
            addToast({ type: "error", message: editThread ? "Failed to update thread" : "Failed to create thread" });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setSaving(true);
        try {
            await disableThread(token, deleteTarget.id);
            addToast({ type: "success", message: "Thread deleted" });
            setDeleteDialogOpen(false);
            setDeleteTarget(null);
            await fetchAll();
        } catch {
            addToast({ type: "error", message: "Failed to delete thread" });
        } finally {
            setSaving(false);
        }
    };

    const updateField = (field: string, value: string | number) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const updateColor = (channel: "r" | "g" | "b", value: number) => {
        setForm(prev => ({ ...prev, color: { ...prev.color, [channel]: value } }));
    };

    const numField = (label: string, field: string, value: number) => (
        <TextField
            label={label}
            type="number"
            size="small"
            fullWidth
            value={value}
            onChange={e => updateField(field, parseFloat(e.target.value) || 0)}
            sx={{ mb: 2 }}
        />
    );

    const handleSort = (col: "name" | "category" | "startPrice" | "perAdditionalPrice") => {
        if (sortBy === col) {
            setSortDir(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSortBy(col);
            setSortDir("asc");
        }
    };

    const sortedThreads = [...threads].sort((a, b) => {
        if (!sortBy) return 0;
        const av = a[sortBy];
        const bv = b[sortBy];
        const cmp = typeof av === "string" ? av.localeCompare(bv as string) : (av as number) - (bv as number);
        return sortDir === "asc" ? cmp : -cmp;
    });

    if (loading) return <CircularProgress />;

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Threads Management</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                    Add Thread
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sortDirection={sortBy === "name" ? sortDir : false}>
                                <TableSortLabel active={sortBy === "name"} direction={sortBy === "name" ? sortDir : "asc"} onClick={() => handleSort("name")}>Name</TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === "category" ? sortDir : false}>
                                <TableSortLabel active={sortBy === "category"} direction={sortBy === "category" ? sortDir : "asc"} onClick={() => handleSort("category")}>Category</TableSortLabel>
                            </TableCell>
                            <TableCell>Thread Diameter</TableCell>
                            <TableCell>Pitch</TableCell>
                            <TableCell>Min/Max Diameter</TableCell>
                            <TableCell>Min/Max Depth</TableCell>
                            <TableCell sortDirection={sortBy === "startPrice" ? sortDir : false}>
                                <TableSortLabel active={sortBy === "startPrice"} direction={sortBy === "startPrice" ? sortDir : "asc"} onClick={() => handleSort("startPrice")}>Start Price</TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === "perAdditionalPrice" ? sortDir : false}>
                                <TableSortLabel active={sortBy === "perAdditionalPrice"} direction={sortBy === "perAdditionalPrice" ? sortDir : "asc"} onClick={() => handleSort("perAdditionalPrice")}>Per Additional</TableSortLabel>
                            </TableCell>
                            <TableCell>Color</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedThreads.map(t => (
                            <TableRow key={t.id}>
                                <TableCell>{t.name}</TableCell>
                                <TableCell>{t.category}</TableCell>
                                <TableCell>{t.threadDiameter}</TableCell>
                                <TableCell>{t.pitch}</TableCell>
                                <TableCell>{t.minDiameter} / {t.maxDiameter}</TableCell>
                                <TableCell>{t.minDepth} / {t.maxDepth}</TableCell>
                                <TableCell>{(t.startPrice / 100).toFixed(2)}</TableCell>
                                <TableCell>{(t.perAdditionalPrice / 100).toFixed(2)}</TableCell>
                                <TableCell>
                                    <Box
                                        sx={{
                                            width: 24, height: 24, borderRadius: "4px", border: "1px solid #ccc",
                                            backgroundColor: `rgb(${t.color.r},${t.color.g},${t.color.b})`,
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => openEdit(t)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => openDelete(t)}><DeleteIcon fontSize="small" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {threads.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={10} align="center">No threads found</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editThread ? "Edit Thread" : "Create Thread"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField label="Name" size="small" fullWidth value={form.name} onChange={e => updateField("name", e.target.value)} sx={{ mb: 2 }} />
                        <TextField label="Category" size="small" fullWidth value={form.category} onChange={e => updateField("category", e.target.value)} sx={{ mb: 2 }} />
                        {numField("Thread Diameter", "threadDiameter", form.threadDiameter)}
                        {numField("Pitch", "pitch", form.pitch)}
                        {numField("Min Diameter", "minDiameter", form.minDiameter)}
                        {numField("Max Diameter", "maxDiameter", form.maxDiameter)}
                        {numField("Min Depth", "minDepth", form.minDepth)}
                        {numField("Max Depth", "maxDepth", form.maxDepth)}
                        <TextField
                            label="Start Price (DKK)"
                            type="number"
                            size="small"
                            fullWidth
                            value={form.startPrice}
                            onChange={e => {
                                const val = e.target.value;
                                const parts = val.split(".");
                                if (parts.length > 1 && parts[1].length > 2) return;
                                updateField("startPrice", parseFloat(val) || 0);
                            }}
                            inputProps={{ step: "0.01", min: 0 }}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Per Additional Price (DKK)"
                            type="number"
                            size="small"
                            fullWidth
                            value={form.perAdditionalPrice}
                            onChange={e => {
                                const val = e.target.value;
                                const parts = val.split(".");
                                if (parts.length > 1 && parts[1].length > 2) return;
                                updateField("perAdditionalPrice", parseFloat(val) || 0);
                            }}
                            inputProps={{ step: "0.01", min: 0 }}
                            sx={{ mb: 2 }}
                        />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Color (RGB)</Typography>
                        <Box display="flex" gap={1}>
                            <TextField label="R" type="number" size="small" value={form.color.r} onChange={e => updateColor("r", parseInt(e.target.value) || 0)} inputProps={{ min: 0, max: 255 }} />
                            <TextField label="G" type="number" size="small" value={form.color.g} onChange={e => updateColor("g", parseInt(e.target.value) || 0)} inputProps={{ min: 0, max: 255 }} />
                            <TextField label="B" type="number" size="small" value={form.color.b} onChange={e => updateColor("b", parseInt(e.target.value) || 0)} inputProps={{ min: 0, max: 255 }} />
                            <Box sx={{ width: 40, height: 40, borderRadius: "4px", border: "1px solid #ccc", backgroundColor: `rgb(${form.color.r},${form.color.g},${form.color.b})` }} />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : editThread ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Thread</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete "{deleteTarget?.name}"?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ThreadsManagement;
